import {
  Component,
  EventEmitter,
  OnInit,
  Output
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Company } from 'src/app/domains/companies/models';
import { EquipmentContent, Handset } from 'src/app/domains/equipment/models';
import { EquipmentService } from 'src/app/domains/equipment/services';
import { User } from 'src/app/domains/users/models';
import { UsersService } from 'src/app/domains/users/services';
import { NotificationService } from 'src/app/shared/services/notification/notification.service';

@Component({
  selector: 'gdp-add-equipment-popup',
  templateUrl: './add-equipment-popup.component.html',
  styleUrls: ['./add-equipment-popup.component.scss']
})
export class AddEquipmentPopupComponent implements OnInit{
  public currentStep: number = 1;
  public form: UntypedFormGroup;
  public macAddressPattern = /^([0-9A-Fa-f]{12}|([0-9A-Fa-f]{2}-){5}[0-9A-Fa-f]{2}|([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2})$/;
  public ipuiPattern = /^([0-9A-Fa-f]{10}|([0-9A-Fa-f]{2}-){4}[0-9A-Fa-f]{2}|([0-9A-Fa-f]{2}:){4}[0-9A-Fa-f]{2})$/;
  public models: string[] = ['AIR', 'SMART_V1', 'SMART_V2', 'CONTROL'];
  public loadingCompanies: boolean = false;
  public companies: Company[];
  public shownCompanies: Company[];
  public selectedCompany: Company;
  public selectedCompanyName: string = '';
  public loadingUsers: boolean = false;
  public users: User[];
  public selectedUser: User = null;
  public loadingCreate: boolean = false;
  @Output() onAddEquipment: EventEmitter<any>= new EventEmitter();

  constructor(
    private activeModal: NgbActiveModal,
    private fb: UntypedFormBuilder,
    private usersService: UsersService,
    private notification: NotificationService,
    private equipmentService: EquipmentService
  ) {
    this.form = this.fb.group({
      mac_address: new UntypedFormControl(null, [Validators.required, Validators.pattern(this.macAddressPattern)]),
      model: new UntypedFormControl(this.models[0], [Validators.required]),
      company: new UntypedFormControl(null, [Validators.required])
    });
  }

  ngOnInit() {}

  isFormValid() {
    const model = this.form.get('model').value;
    const company = this.form.get('company');
    const mac = this.form.get('mac_address')
    const companyDisabledValid = (!company.valid && !company.invalid && company.disabled);
    if (this.currentStep === 1) {
      return mac.valid && model !== '' && (company.valid || companyDisabledValid)
    } else {
      return model === 'AIR' ? this.form.get('ipui').valid : true;
    }
  }

  isFieldValid(name: string) {
    return (
      !this.form.get(name).valid && this.form.get(name).touched
    );
  }

  selectModel(model: string) {
    this.form.patchValue({ model });
  }

  selectUser(user: User) {
    this.selectedUser = user;
  }

  clickBack() {
    if(this.form.get('model').value === 'AIR') {
      this.form.removeControl('ipui');
    }
    this.currentStep = 1;
  }

  clickNext() {
    if(this.form.get('model').value === 'AIR') {
      this.form.addControl('ipui', new UntypedFormControl(null, [Validators.required, Validators.pattern(this.ipuiPattern)]));
    }
    this.getUsers();
  }

  getUsers() {
    this.loadingUsers = true;
    this.usersService.getUsers(this.form.get('company').value.companyId).subscribe((res: User[]) => {
      this.users = res;
      this.loadingUsers = false;
      this.currentStep = 2;
    }, () => {
      this.loadingUsers = false;
      this.clickBack();
    });
  }

  addEquipment() {
    this.loadingCreate = true;
    const userId = this.selectedUser ? this.selectedUser.pbxId : '';
    this.equipmentService.createEquipment(
      this.form.get('mac_address').value,
      this.form.get('model').value,
      this.form.get('company').value.pbxId,
      userId,
      [
        {
          status: 409,
          message: 'equipment.modals.add-equipment.error-macAddress'
        }
      ]
      ).subscribe((res: EquipmentContent) => {
      if (this.form.get('model').value === 'AIR') {
        this.addHandset(res);
      }
      this.onAddEquipment.emit();
      this.loadingCreate = false;
      this.notification.success('equipment.modals.add-equipment.add-success');
      this.close();
    }, () => {
      this.loadingCreate = false;
      this.close();
    });
  }

  addHandset(equipment: EquipmentContent) {
    if(this.selectedUser) {
      this.associateHandsetToEquipmentAndUser(equipment)
    } else {
      this.associateHandsetToEquipment(equipment);
    }
  }

  associateHandsetToEquipmentAndUser(equipment: EquipmentContent) {
    this.loadingCreate = true;
    this.equipmentService.associateHandsetToEquipmentAndUser(
      equipment.pbx.id,
      this.selectedUser.pbxId,
      equipment.mac_address,
      this.form.get('ipui').value,
      [
        {
          status: 409,
          message: 'equipment.details.modals.add-handset.error-exists'
        }
      ]
    ).subscribe((res: Handset) => {
      this.notification.success('equipment.modals.add-equipment.add-success-handset');
      this.loadingCreate = false;
    }, () => {
      this.loadingCreate = false;
      this.close();
    });
  }

  associateHandsetToEquipment(equipment: EquipmentContent) {
    this.equipmentService.associateHandsetToEquipment(
      equipment.mac_address,
      this.form.get('ipui').value,
      [
        {
          status: 409,
          message: 'equipment.details.modals.add-handset.error-exists'
        }
      ]
    ).subscribe((res: Handset) => {
      this.notification.success('equipment.modals.add-equipment.add-success-handset');
      this.loadingCreate = false;
    }, () => {
      this.loadingCreate = false;
      this.close();
    });
  }

  close() {
    this.activeModal.close();
  }

}
